import { SaktiRkhDetailData, SaktiRkhDetailUpdatePayload } from '../types';

export interface SaktiRkhDetailDraftValidationResult {
  isValid: boolean;
  errors: string[];
}

const isValidApiDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const date = new Date(`${value}T00:00:00Z`);

  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export const createEditDraft = (detail: SaktiRkhDetailData): SaktiRkhDetailData =>
  JSON.parse(JSON.stringify(detail)) as SaktiRkhDetailData;

export const validateRkhDetailDraft = (
  draft: SaktiRkhDetailData
): SaktiRkhDetailDraftValidationResult => {
  const errors: string[] = [];
  const rkhNumber = typeof draft.rkh.rkh_nbr === 'string' ? draft.rkh.rkh_nbr : '';
  const isNonNegativeFiniteNumber = (value: number): boolean =>
    Number.isFinite(value) && value >= 0;
  const ensureNonNegativeFinite = (value: number, path: string): void => {
    if (!isNonNegativeFiniteNumber(value)) {
      errors.push(`${path} must be finite and non-negative`);
    }
  };
  const ensureRequiredText = (value: unknown, path: string): void => {
    if (typeof value !== 'string' || !value.trim()) {
      errors.push(`${path} is required`);
    }
  };

  if (!rkhNumber.trim()) {
    errors.push('rkh.rkh_nbr is required');
  }

  if (!isValidApiDate(draft.rkh.rkh_date)) {
    errors.push('rkh.rkh_date is invalid');
  }

  ensureRequiredText(draft.rkh.stage, 'rkh.stage');

  draft.locations.forEach((location, index) => {
    ensureRequiredText(location.block_code, `locations[${index}].block_code`);
    ensureNonNegativeFinite(location.harvest_area, `locations[${index}].harvest_area`);
    ensureNonNegativeFinite(
      location.harvest_area_target,
      `locations[${index}].harvest_area_target`
    );
    ensureNonNegativeFinite(location.est_weight, `locations[${index}].est_weight`);
    ensureNonNegativeFinite(location.est_bunch, `locations[${index}].est_bunch`);
    ensureNonNegativeFinite(location.est_hk, `locations[${index}].est_hk`);
    ensureNonNegativeFinite(location.est_output, `locations[${index}].est_output`);
    ensureNonNegativeFinite(location.hanca_cnt, `locations[${index}].hanca_cnt`);
  });

  draft.hancas.forEach((hanca, index) => {
    ensureNonNegativeFinite(hanca.hanca_nbr, `hancas[${index}].hanca_nbr`);
  });

  draft.harvesters.forEach((harvester, index) => {
    ensureRequiredText(harvester.nip, `harvesters[${index}].nip`);
    ensureRequiredText(harvester.fp_id, `harvesters[${index}].fp_id`);
    ensureRequiredText(harvester.name, `harvesters[${index}].name`);
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const toRkhDetailUpdatePayload = (draft: SaktiRkhDetailData): SaktiRkhDetailUpdatePayload => ({
  rkh: {
    id: draft.rkh.id,
    rkh_nbr: draft.rkh.rkh_nbr,
    rkh_date: draft.rkh.rkh_date,
    stage: draft.rkh.stage,
    company_id: draft.rkh.company_id,
    estate_id: draft.rkh.estate_id,
    division_id: draft.rkh.division_id,
  },
  locations: draft.locations.map((location) => ({
    id: location.id,
    rkh_id: location.rkh_id,
    block_id: location.block_id,
    block_code: location.block_code,
    is_carry_over: location.is_carry_over,
    harvest_area: location.harvest_area,
    harvest_area_target: location.harvest_area_target,
    est_weight: location.est_weight,
    est_bunch: location.est_bunch,
    est_hk: location.est_hk,
    est_output: location.est_output,
    hanca_cnt: location.hanca_cnt,
  })),
  foremen: draft.foremen.map((foreman) => ({
    id: foreman.id,
    rkh_id: foreman.rkh_id,
    foreman_group_id: foreman.foreman_group_id,
    foreman_id: foreman.foreman_id,
    foreman1_id: foreman.foreman1_id,
    kerani_harvest_id: foreman.kerani_harvest_id,
    is_kutip_required: foreman.is_kutip_required,
  })),
  harvesters: draft.harvesters.map((harvester) => ({
    id: harvester.id,
    foreman_id: harvester.foreman_id,
    emp_id: harvester.emp_id,
    nip: harvester.nip,
    fp_id: harvester.fp_id,
    name: harvester.name,
    job_level_id: harvester.job_level_id,
    job_id: harvester.job_id,
    is_kutip_required: harvester.is_kutip_required,
    is_asistensi: harvester.is_asistensi,
    attendance_type_id: harvester.attendance_type_id,
    time_in: harvester.time_in ?? null,
    time_out: harvester.time_out ?? null,
  })),
  hancas: draft.hancas.map((hanca) => ({
    id: hanca.id,
    location_id: hanca.location_id,
    foreman_id: hanca.foreman_id,
    hanca_nbr: hanca.hanca_nbr,
    harvester_id: hanca.harvester_id,
  })),
});

export const hasDraftChanges = (base: SaktiRkhDetailData, draft: SaktiRkhDetailData): boolean =>
  JSON.stringify(toRkhDetailUpdatePayload(base)) !==
  JSON.stringify(toRkhDetailUpdatePayload(draft));
